/**
 * Form (conteúdo de slide-over) para criar NFC-e — M14, redesign FIN-9.
 *
 * NFC-e (modelo 65, varejo PF). Backend POST /api/notas-fiscais/criar-nfce:
 * cria nota tipo=NFCE PENDENTE; CPF do consumidor é opcional ("consumidor
 * não identificado" é aceito até o limite estadual). Gestor clica "Emitir".
 */
import React, { useEffect, useState } from "react";
import { FiFilePlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { criarNotaFiscalConsumidor } from "../../../services/fiscal";
import { parseDinheiro } from "../../../utils/dinheiro";
import { Input, Select } from "../../Orcamentos/styles";

export default function CriarNfceForm({ onCreated, onCancel }) {
  const { request } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [pedidoId, setPedidoId] = useState("");
  const [cpf, setCpf] = useState("");
  const [valorTotal, setValorTotal] = useState("");

  useEffect(() => {
    setLoadingPedidos(true);
    // Decisão D4 (auditoria 2026-08-07): a nota acompanha a MERCADORIA, então
    // o documento é elegível já na expedição — exigir "entregue" deixava o
    // dropdown vazio (nenhum pedido chegava a ENTREGUE) e o módulo fiscal
    // inteiro inutilizável.
    Promise.all([
      request("/api/pedidos?situacao=EM_EXPEDICAO").catch(() => []),
      request("/api/pedidos?situacao=ENTREGUE").catch(() => []),
    ])
      .then(([emExpedicao, entregues]) => {
        const lista = (p) => (Array.isArray(p) ? p : p?.results || []);
        setPedidos([...lista(emExpedicao), ...lista(entregues)]);
      })
      .catch(() => {})
      .finally(() => setLoadingPedidos(false));
  }, [request]);

  const submit = async () => {
    const payload = { pedido_id: pedidoId };
    const limpo = cpf.replace(/\D/g, "");
    if (limpo) {
      if (limpo.length !== 11) throw new Error("CPF do consumidor deve ter 11 dígitos.");
      payload.cpf_consumidor = limpo;
    }
    // A22: quando o pedido já tem documento fiscal ativo, o backend exige o
    // valor desta nota. Em branco, ele usa o valor do pedido.
    if (valorTotal) {
      const v = parseDinheiro(valorTotal);
      if (!v || v <= 0) throw new Error("Valor da nota deve ser maior que zero.");
      payload.valor_total = v;
    }
    const nova = await criarNotaFiscalConsumidor(request, payload);
    onCreated?.(nova);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Pedido *</span>
        <Select value={pedidoId} onChange={(e) => setPedidoId(e.target.value)}>
          <option value="">{loadingPedidos ? "Carregando…" : "— selecione o pedido —"}</option>
          {pedidos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.numero} · {p.cliente_nome} ({p.situacao_display || p.situacao})
            </option>
          ))}
        </Select>
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Pedidos em expedição ou já entregues — a nota acompanha a mercadoria.
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>CPF do consumidor (opcional)</span>
        <Input value={cpf} onChange={(e) => setCpf(e.target.value)}
               placeholder="deixe em branco para consumidor não identificado" />
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          "Consumidor não identificado" é aceito até o limite estadual (~R$ 10 mil/UF).
          Acima disso, o CPF passa a ser obrigatório.
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          Valor desta nota (opcional)
        </span>
        <Input value={valorTotal}
               onChange={(e) => setValorTotal(e.target.value)}
               placeholder="vazio = valor total do pedido" />
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Só preencha se o pedido já tiver outra nota — a soma dos
          documentos não pode passar o valor do pedido.
        </span>
      </label>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancelar</button>
        <AsyncButton variant="primary" disabled={!pedidoId}
                     successMessage="NFC-e criada — clique Emitir no detalhe"
                     onClick={submit}>
          <FiFilePlus size={14} aria-hidden="true" /> Criar NFC-e
        </AsyncButton>
      </div>
    </div>
  );
}
